import React from "react";
import { FlagCountResponse, GroupCount } from "../../api/Items";
import {
  BehaviorMappingActions,
  behaviorRelabelMapping,
} from "../../api/FamaFlags";
import { useAffiliationManagement } from "../../affiliation/useAffiliationManagement";

type Props = {
  flagsCount: FlagCountResponse | undefined;
  keywordCount: GroupCount | undefined;
};

function TitleWithIconAndText({
  title,
  icon,
  text,
}: {
  title: string;
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="font-semibold text-gray-700 text-lg">{title}</div>
        <div>{icon}</div>
      </div>
      <div className="text-gray-600 font-light">{text}</div>
    </div>
  );
}

function hideFlagBehavior(
  flagsCount: FlagCountResponse | undefined,
  behavior: string,
  behaviorMappingActions: BehaviorMappingActions
) {
  return (
    (flagsCount?.behaviors.groups.find((group) => {
      const relabeledName =
        behaviorRelabelMapping([group.name], behaviorMappingActions)[0] ?? "";
      return relabeledName.toLowerCase() === behavior.toLowerCase();
    })?.count ?? 0) === 0
  );
}

const ReportsSummaryHeaderFlagDescriptions = (props: Props) => {
  const { behaviorMappingActions } = useAffiliationManagement();

  const flagDescriptions = [
    {
      title: "Threats",
      icon: <img src="flags-img/threats.png" alt="violence" className="w-6" />,
      text: "Threats of physical harm or engagement in intimidation.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "THREAT",
        behaviorMappingActions
      ),
    },
    {
      title: "Intolerance",
      icon: (
        <img src="flags-img/intolerance.png" alt="violence" className="w-6" />
      ),
      text: "Racist, sexist, homophobic, xenophobic, or otherwise discriminatory content.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "INTOLERANCE",
        behaviorMappingActions
      ),
    },

    {
      title: "Sex",
      icon: <img src="flags-img/sex.png" alt="violence" className="w-6" />,
      text: "Inappropriate sexual content, from explicit images to suggestive language.",
      hide: hideFlagBehavior(props.flagsCount, "SEX", behaviorMappingActions),
    },

    {
      title: "Violence",
      icon: <img src="flags-img/violence.png" alt="violence" className="w-6" />,
      text: "Advocacy, incitement, or participation in violent acts.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "VIOLENCE",
        behaviorMappingActions
      ),
    },

    {
      title: "Trolling",
      icon: <img src="flags-img/trolling.png" alt="violence" className="w-6" />,
      text: "Online harassment, direct intimidation, or content that bullies, intimidates, ostracizes, shames, name-calls, or insults someone.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "TROLLING",
        behaviorMappingActions
      ),
    },
    {
      title: "Harassment",
      icon: <img src="flags-img/trolling.png" alt="violence" className="w-6" />,
      text: "Online harassment, direct intimidation, or content that bullies, intimidates, ostracizes, shames, name-calls, or insults someone.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "HARASSMENT",
        behaviorMappingActions
      ),
    },

    {
      title: "Crime",
      icon: <img src="flags-img/crime.png" alt="violence" className="w-6" />,
      text: "Apparent criminal activities like theft or gang affiliations.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "CRIMINAL",
        behaviorMappingActions
      ),
    },
    {
      title: "Cannabis",
      icon: <img src="flags-img/cannabis.png" alt="violence" className="w-6" />,
      text: "Mentions or images of cannabis use.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "CANNABIS",
        behaviorMappingActions
      ),
    },

    {
      title: "Illicit Drugs",
      icon: <img src="flags-img/drugs.png" alt="violence" className="w-6" />,
      text: "Content promoting drugs or drug use, or use of illegal substances.",
      hide: hideFlagBehavior(
        props.flagsCount,
        "OTHER DRUGS",
        behaviorMappingActions
      ),
    },

    {
      title: "Community",
      icon: (
        <img src="flags-img/community.png" alt="violence" className="w-6" />
      ),
      text: "Positive community involvement including volunteer work, charitable activities, and contributions. ",
      hide: hideFlagBehavior(
        props.flagsCount,
        "COMMUNITY",
        behaviorMappingActions
      ),
    },

    {
      title: "Keywords",
      icon: <img src="flags-img/keywords.png" alt="violence" className="w-6" />,
      text: "Screening requesters may include specific keywords to flag in alignment with their internal policies.",
      hide: props.keywordCount?.total === 0,
    },
  ];

  if (flagDescriptions.filter((flag) => !flag.hide).length === 0) {
    return (
      <div className="text-gray-600">Report Contains No Behavior Flags</div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-y-7 gap-x-12">
      {flagDescriptions
        .filter((flag) => !flag.hide)
        .map((flag) => (
          <TitleWithIconAndText
            key={flag.title}
            title={flag.title}
            icon={flag.icon}
            text={flag.text}
          />
        ))}
    </div>
  );
};

export default ReportsSummaryHeaderFlagDescriptions;
